import { useEffect, useState, type FormEvent } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router';

import { useAuth } from '../contexts/AuthContext';
import { ApiError, activateApi } from '../services/api/client';

/**
 * Ativação de conta pré-cadastrada (PRD 33.7).
 *
 * Chega aqui só quem tem o link — saber o e-mail não basta. Mostra de quem é
 * a conta antes de pedir a senha, para o aluno confirmar que é ele mesmo.
 */
export default function ActivatePage() {
  const { token = '' } = useParams();
  const { user, loading, refresh } = useAuth();
  const navigate = useNavigate();

  const [target, setTarget] = useState<{ name: string; email: string } | null>(null);
  const [error, setError] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    activateApi
      .peek(token)
      .then(setTarget)
      .catch((err: unknown) =>
        setError(err instanceof ApiError ? err.message : 'Não foi possível ler este link.'),
      );
  }, [token]);

  if (loading) {
    return (
      <div className="centered-state">
        <p>Carregando…</p>
      </div>
    );
  }

  if (user) {
    return <Navigate to="/" replace />;
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError('');
    setSubmitting(true);

    try {
      await activateApi.activate(token, password);
      await refresh();
      void navigate('/', { replace: true });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Não foi possível ativar a conta.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="auth-page">
      <div className="auth-card">
        <p className="eyebrow">Gestão de Processos</p>
        <h1>Ativar sua conta</h1>

        {error && !target && (
          <p className="form-error" role="alert">
            {error}
          </p>
        )}

        {!target && !error && <p className="muted">Verificando o link…</p>}

        {target && (
          <>
            <p className="muted">
              Definindo a senha de <strong>{target.name}</strong> ({target.email}).
            </p>

            <form onSubmit={handleSubmit} noValidate>
              <label htmlFor="password">Senha</label>
              <input
                id="password"
                type="password"
                autoComplete="new-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
              <p className="field-hint">Mínimo de 8 caracteres.</p>

              {error && (
                <p className="form-error" role="alert">
                  {error}
                </p>
              )}

              <button type="submit" className="primary" disabled={submitting}>
                {submitting ? 'Ativando…' : 'Ativar conta'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
